/**
 * The demonstration inputs for the signed-in account's jurisdiction.
 *
 * District, governing-body and STP inputs are NOT compiled into the web bundle:
 * the API returns only the districts, bodies and STPs the session may see
 * (GET /api/v1/jurisdiction/dataset), and `DatasetGate` hands them to
 * `setDatasetInputs` BEFORE the demo application chunk is imported. The modules
 * that derive every record (data.ts, stps.ts, …) then read them once, at
 * import, exactly as they used to read the compiled-in constants.
 *
 * This module must stay free of imports (other than types) so it can load
 * before the rest of the hierarchy.
 */
import type { DistrictInput, GoverningBodyInput } from "./data"
import type { InventoryOnlySpec, StpSpec } from "./stps"
import type { StpNetworkGroup } from "./types"

/** ALL: every district (presenter) · DISTRICT: one district and its bodies · BODY: one body. */
export type DatasetScopeKind = "ALL" | "DISTRICT" | "BODY"

export interface DatasetInputs {
  version: number
  scope_kind: DatasetScopeKind
  districts: DistrictInput[]
  bodies: GoverningBodyInput[]
  stp_specs: Record<string, StpSpec[]>
  inventory_only_specs: Record<string, InventoryOnlySpec[]>
  network_group_specs: Omit<StpNetworkGroup, "combined_capacity_mld">[]
}

const KEY = "__wicpDatasetInputs"
type Holder = { [KEY]?: DatasetInputs }

/** Called once per page load (a different account always means a fresh page load). */
export function setDatasetInputs(inputs: DatasetInputs): void {
  ;(globalThis as Holder)[KEY] = inputs
}

export function hasDatasetInputs(): boolean {
  return (globalThis as Holder)[KEY] !== undefined
}

export function datasetInputs(): DatasetInputs {
  const inputs = (globalThis as Holder)[KEY]
  if (!inputs) throw new Error("Jurisdiction dataset not loaded: the demo modules must be imported after DatasetGate")
  return inputs
}

/** District-wide views (whole district, district summaries) need a district or wider jurisdiction. */
export function canViewDistrict(): boolean {
  return datasetInputs().scope_kind !== "BODY"
}

/** More than one district (presenter): cross-district totals and district switching. */
export function canViewAllDistricts(): boolean {
  return datasetInputs().scope_kind === "ALL"
}
